import { useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { isOfflineGap } from '@/offline/availability';
import { useBatches, useOnHand, type SkuRow } from '@/hooks/inventory';
import { useQueuedOut } from '@/offline/projection';
import type { SearchSelectHandle } from './SearchSelect';
import { ItemPicker, ItemPickerHead, type ItemSearch } from './ItemPicker';
import { quantity, shortDate } from './ui';
import { useLineRemoval } from './lineRows';
import { PackedQtyInput } from './PackedQtyInput';
import { packagingsOf, packedSuffix } from '@/packing';

/**
 * A line drawing down stock already on hand — issuing, transferring or
 * adjusting an existing lot — as opposed to `ReceiptLine`, which records a
 * new one. The lot is a choice among what the store actually holds, never
 * free text, and there is no cost: whatever it is worth is decided by the
 * document that receives or averages it, not typed here.
 */
export interface DrawLine {
  key: string;
  skuId: string;
  skuLabel: string;
  batchId: string;
  qty: string;
}

let lineCounter = 0;

export function emptyDrawLine(patch: Partial<DrawLine> = {}): DrawLine {
  lineCounter += 1;
  return { key: `draw-${lineCounter}`, skuId: '', skuLabel: '', batchId: '', qty: '', ...patch };
}

/** Nothing typed or chosen on the line yet. */
function isBlankDraw(l: DrawLine): boolean {
  return [l.skuId, l.batchId, l.qty].every((v) => v.trim() === '');
}

function DrawLineRow({
  line,
  index,
  storeId,
  locked,
  skus,
  search,
  style,
  autoFocus,
  checkAvailable,
  lotOptional,
  drawnElsewhere,
  itemRef,
  qtyRef,
  canRemove,
  removeRef,
  onUpdate,
  onRemove,
  onKeyDown,
  onPick,
}: {
  line: DrawLine;
  index: number;
  storeId: string;
  locked: boolean;
  skus: ReadonlyMap<string, SkuRow>;
  search: ItemSearch;
  style: CSSProperties;
  autoFocus: boolean;
  checkAvailable: boolean;
  lotOptional: boolean;
  /** How much of the same stock other lines of this document already take,
   *  so the remaining figure is what will really be left once it posts. */
  drawnElsewhere: number;
  itemRef: (handle: SearchSelectHandle | null) => void;
  qtyRef: (el: HTMLInputElement | null) => void;
  /** False for a blank row the grid would only put straight back. */
  canRemove: boolean;
  removeRef: (el: HTMLButtonElement | null) => void;
  onUpdate: (patch: Partial<DrawLine>) => void;
  onRemove: () => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
  onPick: () => void;
}) {
  const sku = skus.get(line.skuId);
  const tracked = sku?.isBatchTracked ?? false;
  const lotsQuery = useBatches(storeId, line.skuId, tracked);
  const { data: batches, dataUpdatedAt: lotsReadAt } = lotsQuery;
  // Offline, the lots of an item in a store this PC never read in full.
  const lotsNotHere = isOfflineGap(lotsQuery);
  const lot = tracked ? line.batchId || undefined : undefined;
  const { data: held, dataUpdatedAt: heldReadAt } = useOnHand(storeId, line.skuId, lot);
  // What this PC's own unsent work already takes out of the store (.docs/023):
  // left out, two issues typed offline could each look as if they fit.
  const queuedOutOf = useQueuedOut();
  const unsent = held !== undefined && line.skuId ? queuedOutOf(storeId, line.skuId, lot, heldReadAt) : 0;
  const onHand = held === undefined ? undefined : held - unsent;
  const available = sku && onHand !== undefined ? quantity(String(onHand), sku.uomPrecision) : null;
  const typed = Number(line.qty);
  const drawing = line.qty !== '' && Number.isFinite(typed) && typed > 0;
  // What is left once this line and any other line on the same stock post.
  const left = onHand !== undefined ? onHand - drawnElsewhere - (drawing ? typed : 0) : undefined;
  const over = checkAvailable && left !== undefined && (drawing || drawnElsewhere > 0) && left < 0;
  const fmt = (n: number) => quantity(String(n), sku?.uomPrecision ?? 2);
  const elsewhere = drawnElsewhere > 0 ? ` (${fmt(drawnElsewhere)} on other lines)` : '';
  const waiting = unsent > 0 ? `, after ${fmt(unsent)} in work not sent yet` : '';

  let hint: string | null = null;
  if (sku) {
    // In packages too, when the item has them: "60 KG held (1 Carton + 10 Packs)".
    const held = onHand !== undefined ? packedSuffix(onHand, sku) : '';
    if (over && left !== undefined) hint = `⚠ only ${available} held${held}${waiting} — ${fmt(-left)} ${sku.uom} short${elsewhere}`;
    else if (checkAvailable && left !== undefined && (drawing || drawnElsewhere > 0))
      hint = `${fmt(left)} ${sku.uom} left${packedSuffix(left, sku)} of ${available}${waiting}${elsewhere}`;
    else hint = available ? `${available} ${sku.uom} held${held}${waiting}` : sku.uom;
  }

  return (
    <div className="itemlines__row itemlines__line" style={style}>
      <div className="itemlines__cell itemlines__cell--item">
        <ItemPicker
          ref={itemRef}
          index={index}
          disabled={locked || !storeId}
          value={line.skuId}
          selectedLabel={line.skuLabel}
          search={search}
          placeholder={storeId ? undefined : 'Choose a store first'}
          autoFocus={autoFocus}
          onChange={(option) => onUpdate({ skuId: option?.value ?? '', skuLabel: option?.label ?? '', batchId: '' })}
          onPick={onPick}
        />
      </div>

      <div className="itemlines__cell">
        {tracked ? (
          <label className="field">
            <span className="itemlines__label">Lot<span className="sr-only"> for item {index + 1}</span></span>
            <select
              className="field__input"
              required={!lotOptional}
              disabled={locked}
              value={line.batchId}
              onChange={(e) => onUpdate({ batchId: e.target.value })}
            >
              <option value="">{lotsNotHere ? 'Lots not loaded on this PC' : !batches ? 'Loading…' : lotOptional ? 'Any lot (oldest first)' : 'Choose a lot…'}</option>
              {(batches ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.batchNo} —{' '}
                  {quantity(String(Number(b.qty) - queuedOutOf(storeId, line.skuId, b.id, lotsReadAt)), sku?.uomPrecision ?? 2)} on
                  hand, received {shortDate(b.receivedAt)}
                  {b.expiryDate ? `, expires ${shortDate(b.expiryDate)}` : ''}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <span className="itemlines__na" title={sku ? 'This item is not tracked by lot' : 'Choose an item first'}>
            —
          </span>
        )}
      </div>

      <div className="itemlines__cell">
        <PackedQtyInput
          label="Quantity"
          srLabel={` for item ${index + 1}`}
          labelClassName="itemlines__label"
          item={sku}
          itemKey={line.skuId}
          remember="draw"
          required
          disabled={locked}
          value={line.qty}
          onChange={(qty) => onUpdate({ qty })}
          inputRef={qtyRef}
          onKeyDown={onKeyDown}
          showTotal={packagingsOf(sku).length > 0}
        />
        {hint && (
          <span className={over ? 'itemlines__hint errornote' : 'itemlines__hint'} aria-live="polite">
            {hint}
          </span>
        )}
      </div>

      {!locked && (
        <div className="itemlines__cell">
          {canRemove && (
            <button
              ref={removeRef}
              type="button"
              className="btn btn--ghost itemlines__remove"
              aria-label={`Remove item ${index + 1}`}
              title="Remove this line"
              onClick={onRemove}
            >
              ✕
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The line items of a document that draws down stock already on hand —
 * issuing, transferring or adjusting several items in one pass, the same
 * "type, Enter, type, Enter" rhythm `ItemLines` gives a receipt.
 */
export function DrawLines({
  lines,
  onChange,
  skus,
  search,
  storeId,
  locked = false,
  allowAdd = true,
  checkAvailable = true,
  lotOptional = false,
  minRows = 1,
}: {
  lines: DrawLine[];
  onChange: (lines: DrawLine[]) => void;
  skus: ReadonlyMap<string, SkuRow>;
  search: ItemSearch;
  /** Which store's stock is being drawn from — every lot lookup and
   *  available-quantity hint is scoped to it. */
  storeId: string;
  locked?: boolean;
  /** False for a document whose shape is fixed to one line (editing an
   *  existing issue, say) — the fields stay editable, there is just nothing
   *  to add a second line to. */
  allowAdd?: boolean;
  /** False when the quantity is not necessarily coming out of stock — an
   *  adjustment that increases it, say — so "only N held" would be a
   *  warning about nothing. */
  checkAvailable?: boolean;
  /** True when a lot-tracked line may leave the lot blank and let the
   *  document draw from the oldest lots first (FIFO) instead. */
  lotOptional?: boolean;
  /** Rows the grid never drops below: removing a line under it puts a blank
   *  one back at the bottom. */
  minRows?: number;
}) {
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const needsLot = lines.some((l) => skus.get(l.skuId)?.isBatchTracked);

  const columns = ['minmax(19rem, 4fr)', 'minmax(11rem, 1.4fr)', 'minmax(9rem, 1fr)', ...(locked ? [] : ['var(--target-size)'])].join(
    ' ',
  );
  const gridStyle = { '--itemlines-cols': columns } as CSSProperties;

  function update(key: string, patch: Partial<DrawLine>) {
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  const { canRemove, remove, buttonRef } = useLineRemoval(lines, onChange, emptyDrawLine, isBlankDraw, minRows);

  const itemRefs = useRef(new Map<string, SearchSelectHandle | null>());
  const qtyRefs = useRef(new Map<string, HTMLInputElement | null>());

  function addLine() {
    const added = emptyDrawLine();
    setFocusKey(added.key);
    onChange([...lines, added]);
  }

  /**
   * Enter on a line's Quantity box moves to the next line's Item box, and on
   * the last line adds a fresh one and focuses that — so a form that opens
   * with blank rows ready is typed down rather than added to. Walking between
   * lines that already exist does not depend on `allowAdd`; only appending
   * one does.
   */
  function onLineKeyDown(e: ReactKeyboardEvent<HTMLInputElement>, line: DrawLine, index: number) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const next = lines[index + 1];
    if (next) {
      itemRefs.current.get(next.key)?.focus();
      return;
    }
    if (!allowAdd) return;
    if (!line.skuId || !(Number(line.qty) > 0)) {
      e.currentTarget.reportValidity();
      return;
    }
    addLine();
  }

  const filled = lines.filter((l) => l.skuId && l.qty).length;

  /**
   * How much the other lines take from the same stock as `line`. A line with
   * no lot draws on the item's whole balance, so every other line of that
   * item counts against it; a line on one lot only shares with lines on that
   * same lot.
   */
  function drawnElsewhere(line: DrawLine): number {
    if (!line.skuId) return 0;
    return lines.reduce((sum, other) => {
      if (other.key === line.key || other.skuId !== line.skuId) return sum;
      if (line.batchId && other.batchId !== line.batchId) return sum;
      const n = Number(other.qty);
      return Number.isFinite(n) && n > 0 ? sum + n : sum;
    }, 0);
  }

  return (
    <div className="itemlines">
      <div className="itemlines__row itemlines__head" style={gridStyle} aria-hidden="true">
        <ItemPickerHead />
        {/* Every row has a Lot cell (a dash until an item needs one), so the
            column is always named — or Quantity would sit over it. */}
        <span>{needsLot && !lotOptional ? 'Lot *' : 'Lot'}</span>
        <span>Quantity *</span>
        {!locked && <span />}
      </div>

      {lines.map((line, index) => (
        <DrawLineRow
          key={line.key}
          line={line}
          index={index}
          storeId={storeId}
          locked={locked}
          skus={skus}
          search={search}
          style={gridStyle}
          checkAvailable={checkAvailable}
          lotOptional={lotOptional}
          drawnElsewhere={checkAvailable ? drawnElsewhere(line) : 0}
          autoFocus={focusKey === line.key}
          itemRef={(handle) => itemRefs.current.set(line.key, handle)}
          qtyRef={(el) => qtyRefs.current.set(line.key, el)}
          canRemove={canRemove(line)}
          removeRef={buttonRef(line.key)}
          onUpdate={(patch) => update(line.key, patch)}
          onRemove={() => remove(line.key)}
          onKeyDown={(e) => onLineKeyDown(e, line, index)}
          onPick={() => qtyRefs.current.get(line.key)?.focus()}
        />
      ))}

      <div className="itemlines__foot">
        {!locked && allowAdd ? (
          <button type="button" className="btn" onClick={addLine}>
            + Add item
          </button>
        ) : (
          <span />
        )}
        <div className="itemlines__totals" aria-live="polite">
          <span className="muted">
            {filled} {filled === 1 ? 'line' : 'lines'}
          </span>
        </div>
      </div>
    </div>
  );
}
